import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BillingFormModal } from '../Components/BillingFormModal';
import axiosInstance from '../Core/RestHelper';

interface PlanPerks {
  projects: number | string;
  deployment_targets: string;
  cicd_pipelines: boolean;
  ssl_custom_domains: boolean;
  all_infra_one_view: boolean;
  multi_aws_accounts: number | string;
  monitor_dashboard: string | null;
  cost_monitor: string | null;
  cost_alerts: boolean | number | string;
  cost_anomaly_detection: string | null;
  team_seats: number | string;
  preview_environments: string | null;
  support: string;
}

interface Plan {
  plan_name: string;
  active: boolean
  price: number;
  deployments_allowed_per_month: string;
  perks: PlanPerks;
  payment_plan_id: string | null;
  plan_id: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatPlanName = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);

const planMeta: Record<string, { desc: string; target: string; popular?: boolean; color: string; accentColor: string }> = {
  free:         { desc: 'Evaluate with real apps',           target: '',                 color: '#26262f', accentColor: '#8e8ea8' },
  starter:      { desc: 'Solo founders, small teams',        target: 'Solo founders',    color: '#26262f', accentColor: '#6366f1' },
  professional: { desc: 'Funded startups, 10–50 eng',        target: 'Growing teams',    popular: true, color: '#1d3461', accentColor: '#3b82f6' },
  enterprise:   { desc: 'Dev shops, multi-client AWS',       target: 'Agencies',         color: '#26262f', accentColor: '#a855f7' },
};

const formatVal = (val: number | string, suffix = '') => {
  if (val === -1 || val === 'unlimited') return 'Unlimited';
  if (val === 0 || val === null || val === undefined) return '—';
  return `${val}${suffix}`;
};

const formatCostAlerts = (val: boolean | number | string) => {
  if (val === false || val === null || val === undefined) return '—';
  if (val === -1 || val === 'unlimited' || val === 'unlimited_per_client') return 'Unlimited alerts';
  if (typeof val === 'number') return `${val} alert rules`;
  if (typeof val === 'string') return val;
  return 'Included';
};

const formatMonitor = (val: string | null) => {
  if (!val) return null;
  const map: Record<string, string> = {
    basic: 'Basic',
    advanced: 'Advanced',
    per_client: 'Per client',
  };
  return map[val] || val;
};

const formatCostMonitor = (val: string | null) => {
  if (!val) return null;
  const map: Record<string, string> = {
    view_only: 'View only',
    full_dashboard: 'Full dashboard',
    per_client_view: 'Per client view',
  };
  return map[val] || val;
};

const formatSupport = (val: string) => {
  const map: Record<string, string> = {
    community: 'Community',
    '48h_email': '48h email',
    '12h_email_chat': '12h email + chat',
    '8h_dedicated_slack': '8h + dedicated Slack',
  };
  return map[val] || val;
};

const formatPrice = (price: number, planName: string) => {
  if (planName.toLowerCase() === 'free') return { main: '$0', sub: 'Forever free' };
  if (planName.toLowerCase() === 'enterprise') return { main: `$${price}`, sub: '/mo · or contact us' };
  return { main: `$${price}`, sub: '/mo' };
};

// ── Sub-components ────────────────────────────────────────────────────────────

const CheckIcon = ({ color = '#22c55e', size = 13 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 13 13" fill="none" stroke={color} strokeWidth="2">
    <path d="M2 6.5l4 4 5-5"/>
  </svg>
);

const CrossIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 13 13" fill="none" stroke="#3a3a4a" strokeWidth="1.5">
    <path d="M3 3l7 7M10 3L3 10"/>
  </svg>
);

const RoadmapBadge = () => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', padding: '1px 6px',
    borderRadius: '4px', fontSize: '9.5px', fontWeight: 700,
    background: 'rgba(34,197,94,.1)', color: '#22c55e',
    border: '1px solid rgba(34,197,94,.2)', letterSpacing: '.04em',
    textTransform: 'uppercase' as const,
  }}>
    Roadmap
  </span>
);

const PerkRow = ({ label, value }: { label: string; value: string | boolean | null }) => {
 const enabled = value !== null && value !== false && value !== '—' && value !== 'roadmap';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '5px 0', borderBottom: '1px solid #1a1a22' }}>
      <span style={{ fontSize: '11.5px', color: '#6b6b80', flexShrink: 0 }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: enabled ? '#c8c8d8' : '#3a3a4a', fontWeight: enabled ? 500 : 400 }}>
        {enabled ? (
          typeof value === 'boolean'
            ? <CheckIcon color="#22c55e" size={11}/>
            : <><CheckIcon color="#22c55e" size={11}/>{value}</>
        ) : (
          <CrossIcon size={11}/>
        )}
      </span>
    </div>
  );
};

const card: React.CSSProperties = {
  background: '#111116',
  border: '1px solid #26262f',
  borderRadius: '10px',
  padding: '18px',
};

const sectionTitle: React.CSSProperties = {
  fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '14px',
};

const btn = (variant: 'primary' | 'secondary' | 'blue' = 'secondary'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  padding: '7px 14px', borderRadius: '6px', fontSize: '12.5px',
  fontWeight: 500, cursor: 'pointer', border: 'none',
  fontFamily: "'Inter',sans-serif", transition: 'all .15s',
  ...(variant === 'primary'
    ? { background: '#6366f1', color: '#fff' }
    : variant === 'blue'
    ? { background: '#3b82f6', color: '#fff' }
    : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
});

const billingHistory = [
  { date: new Date(2026, 0, 1), amount: '$59.00', status: 'Paid' },
  { date: new Date(2025, 11, 1), amount: '$59.00', status: 'Paid' },
  { date: new Date(2025, 10, 1), amount: '$59.00', status: 'Paid' },
];

type Tab = 'plans' | 'billing';

// ── Plan Cards Grid (shared between embedded and standalone) ──────────────────

interface PlanCardsProps {
  allPlans: Plan[];
  currentPlan: Plan | null;
  onUpgrade: (plan: Plan) => void;
  navigate: ReturnType<typeof useNavigate>;
}

const PlanCardsSection: React.FC<PlanCardsProps> = ({ allPlans, currentPlan, onUpgrade, navigate }) => (
  <>
    {/* Current plan banner */}
    {currentPlan && (
      <div style={{
        ...card, marginBottom: '20px',
        background: 'linear-gradient(135deg,rgba(99,102,241,.08),rgba(168,85,247,.08))',
        border: '1px solid rgba(99,102,241,.22)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#818cf8', marginBottom: '4px' }}>
              Current Plan
            </div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '18px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>
              {formatPlanName(currentPlan.plan_name)}
              <span style={{ fontFamily: "'Inter',sans-serif", fontSize: '14px', fontWeight: 400, color: '#818cf8', marginLeft: '10px' }}>
                {currentPlan.price === 0 ? 'Free' : `$${currentPlan.price}/mo`}
              </span>
            </div>
            <div style={{ fontSize: '12.5px', color: '#8e8ea8' }}>
              {formatVal(currentPlan.perks.projects)} projects
              {' · '}
              {formatSupport(currentPlan.perks.support)} support
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '6px', marginRight: '8px' }}>
              {[
                { label: 'CI/CD', val: currentPlan.perks.cicd_pipelines },
                // { label: 'Auto-rollback', val: currentPlan.perks.auto_rollback },
                { label: 'Cost monitor', val: !!currentPlan.perks.cost_monitor },
                { label: 'Infra view', val: currentPlan.perks.all_infra_one_view },
              ].map(p => (
                <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: p.val ? '#22c55e' : '#52525f' }}>
                  {p.val ? <CheckIcon color="#22c55e" size={11}/> : <CrossIcon size={11}/>}
                  {p.label}
                </div>
              ))}
            </div>
            {/* <button style={btn('secondary')} onClick={() => navigate('/plans')}>View Billing</button> */}
          </div>
        </div>
      </div>
    )}

    {/* Plan cards */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.min(Math.max(allPlans.length, 1), 4)},1fr)`,
      gap: '12px',
      marginBottom: '20px',
    }}>
      {allPlans.map(plan => {
        const isCurrentPlan = currentPlan?.plan_id === plan.plan_id;
        const meta = planMeta[plan.plan_name.toLowerCase()] || { desc: '', color: '#26262f', accentColor: '#6366f1' };
        const isPopular = !!meta.popular;
        const { main: priceMain, sub: priceSub } = formatPrice(plan.price, plan.plan_name);
        const p = plan.perks;

        return (
          <div
            key={plan.plan_id}
            style={{
              background: isPopular ? 'linear-gradient(160deg, #0f1f3d 0%, #111116 60%)' : '#111116',
              border: `1px solid ${isCurrentPlan ? '#6366f1' : isPopular ? '#3b82f6' : '#26262f'}`,
              borderRadius: '10px', padding: '20px', position: 'relative' as const,
              display: 'flex', flexDirection: 'column',
            }}
          >
            {(isCurrentPlan || isPopular) && (
              <div style={{
                position: 'absolute' as const, top: '-11px', left: '50%', transform: 'translateX(-50%)',
                padding: '3px 12px', borderRadius: '20px', fontSize: '10px', fontWeight: 700,
                textTransform: 'uppercase' as const, letterSpacing: '.06em', whiteSpace: 'nowrap' as const,
                background: isCurrentPlan ? '#6366f1' : '#3b82f6', color: '#fff',
              }}>
                {isCurrentPlan ? 'Current Plan' : 'Most Popular'}
              </div>
            )}

            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5', marginBottom: '2px' }}>
              {formatPlanName(plan.plan_name)}
            </div>
            <div style={{ fontSize: '11.5px', color: '#52525f', marginBottom: '14px' }}>{meta.desc}</div>

            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                <span style={{ fontFamily: "'Syne',sans-serif", fontSize: '30px', fontWeight: 800, color: '#eeeef5', lineHeight: 1 }}>
                  {priceMain}
                </span>
                <span style={{ fontSize: '12px', color: '#52525f' }}>{priceSub}</span>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #26262f', margin: '0 0 14px' }}/>

            <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '6px' }}>Deployment</div>
            <PerkRow label="Projects" value={formatVal(p.projects)} />
            <PerkRow label="Targets" value={p.deployment_targets} />
            <PerkRow label="CI/CD pipelines" value={p.cicd_pipelines} />
            {/* <PerkRow label="Auto-rollback" value={p.auto_rollback} /> */}
            <PerkRow label="SSL / custom domains" value={p.ssl_custom_domains} />

            <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', margin: '12px 0 6px' }}>Infrastructure</div>
            <PerkRow label="All infra in one view" value={p.all_infra_one_view} />
            <PerkRow label="AWS accounts" value={p.multi_aws_accounts === 0 ? null : formatVal(p.multi_aws_accounts)} />
            <PerkRow label="Monitor dashboard" value={formatMonitor(p.monitor_dashboard)} />

            <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', margin: '12px 0 6px' }}>
              Cost Intelligence
            </div>
            <PerkRow label="Cost monitor" value={formatCostMonitor(p.cost_monitor)} />
            <PerkRow label="Cost alerts" value={formatCostAlerts(p.cost_alerts)} />
            <PerkRow
              label="Anomaly detection"
              value={p.cost_anomaly_detection}

            />

            <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', margin: '12px 0 6px' }}>Team & Access</div>
            <PerkRow label="Team seats" value={formatVal(p.team_seats)} />
            <PerkRow
              label="Preview environments"
              value={p.preview_environments}

            />

            <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', margin: '12px 0 6px' }}>Support</div>
            <PerkRow label="Response time" value={formatSupport(p.support)} />

            <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
              {isCurrentPlan ? (
                <button disabled style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500, background: '#1e1e27', color: '#52525f', border: 'none', cursor: 'not-allowed' }}>
                  Current Plan ✓
                </button>
              ) : (
                <button
                  onClick={() => onUpgrade(plan)}
                  style={{
                    width: '100%', padding: '8px', borderRadius: '6px', fontSize: '12.5px',
                    fontWeight: 600, border: 'none', cursor: 'pointer',
                    background: isPopular ? '#3b82f6' : plan.plan_name === 'enterprise' ? '#a855f7' : '#6366f1',
                    color: '#fff',
                  }}
                >
                  {plan.plan_name.toLowerCase() === 'free'
                    ? 'Downgrade to Free'
                    : plan.plan_name.toLowerCase() === 'enterprise'
                    ? 'Contact Us →'
                    : `Upgrade to ${formatPlanName(plan.plan_name)}`}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>

    {/* Security strip */}
    <div style={{ ...card, textAlign: 'center' as const }}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '13.5px', fontWeight: 700, color: '#eeeef5', marginBottom: '16px' }}>
        Enterprise-Grade Security & Compliance
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px' }}>
        {[
          { icon: '🛡️', title: 'SOC 2 Type II', sub: 'Independently audited' },
          { icon: '🔐', title: 'AES-256 Encryption', sub: 'In transit and at rest' },
          { icon: '⏱️', title: '99.9% Uptime SLA', sub: 'With automated failover' },
        ].map(item => (
          <div key={item.title}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>{item.icon}</div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '3px' }}>{item.title}</div>
            <div style={{ fontSize: '11.5px', color: '#8e8ea8' }}>{item.sub}</div>
          </div>
        ))}
      </div>
    </div>
  </>
);

const DowngradeBlockedModal = ({ onClose }: { onClose: () => void }) => (
  <div
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}
    onClick={onClose}
  >
    <div
      style={{ ...card, maxWidth: '360px', width: '90%', textAlign: 'center' as const }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ fontSize: '28px', marginBottom: '10px' }}>⚠️</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5', marginBottom: '8px' }}>
        Cannot Downgrade Plan
      </div>
      <div style={{ fontSize: '12.5px', color: '#8e8ea8', marginBottom: '18px', lineHeight: 1.5 }}>
        Downgrading isn't available here. Please contact customer support to downgrade your plan.
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <button style={btn('secondary')} onClick={onClose}>Close</button>
        <button
          style={btn('primary')}
          onClick={() => { window.location.href = 'https://qixdeploy.com/contact'; }}
        >
          Contact Support
        </button>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

interface PlansPageProps {
  embedded?: boolean;
}

export const PlansPage: React.FC<PlansPageProps> = ({ embedded = false }) => {
  const [tab, setTab] = useState<Tab>('plans');
  const [allPlans, setAllPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showDowngradeBlocked, setShowDowngradeBlocked] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, dashRes] = await Promise.all([
          axiosInstance.get('/admin/plans/'),
          axiosInstance.get('/users/dashboard'),
        ]);
        setAllPlans(
        plansRes.data
          .filter((p: Plan) => p.active)
          .sort((a: Plan, b: Plan) => a.price - b.price)
      );
        console.log(currentPlan)
        setCurrentPlan(dashRes.data.plan_details);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleUpgrade = (plan: Plan) => {
  if (plan.plan_id === currentPlan?.plan_id) return;

  if (plan.plan_name === 'enterprise') {
    window.location.href = 'https://qixdeploy.com/contact';
    return;
  }

  // Block downgrades — plan clicked is cheaper than current plan
  if (currentPlan && plan.price < currentPlan.price) {
    setShowDowngradeBlocked(true);
    return;
  }

  setSelectedPlan(plan);
  setShowBillingModal(true);
};

  const loadingEl = (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '12px',
      ...(embedded ? { padding: '40px 0' } : { minHeight: '100vh', background: '#09090e' }),
    }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite' }}/>
      <span style={{ fontSize: '13px', color: '#52525f' }}>Loading…</span>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (isLoading) return loadingEl;

  // ── EMBEDDED MODE: just the plans grid, no chrome ─────────────────────────
 if (embedded) {
  return (
    <>
      <PlanCardsSection
        allPlans={allPlans}
        currentPlan={currentPlan}
        onUpgrade={handleUpgrade}
        navigate={navigate}
      />
      {showBillingModal && selectedPlan && (
        <BillingFormModal
          isOpen={showBillingModal}
          onClose={() => { setShowBillingModal(false); setSelectedPlan(null); }}
          planId={selectedPlan.plan_id}
          planName={formatPlanName(selectedPlan.plan_name)}
        />
      )}
      {showDowngradeBlocked && (
        <DowngradeBlockedModal onClose={() => setShowDowngradeBlocked(false)} />
      )}
    </>
  );
}

  // ── STANDALONE MODE: full page with topbar, tabs, billing tab ────────────
  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>

      {/* Topbar */}
      <div style={{ height: '50px', background: '#111116', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', padding: '0 20px', position: 'sticky' as const, top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' }}>
          <span>Home</span>
          <span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>Plans & Billing</span>
        </div>
      </div>

      <div style={{ padding: '20px 24px', maxWidth: '1060px', margin: '0 auto' }}>

        {/* Page header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>Plans & Billing</h1>
          <p style={{ fontSize: '13px', color: '#8e8ea8' }}>Manage your subscription plan and billing information</p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1px', background: '#1e1e27', padding: '3px', borderRadius: '6px', width: 'fit-content', marginBottom: '20px' }}>
          {(['plans', 'billing'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '5px 16px', borderRadius: '4px', fontSize: '13px',
                fontWeight: 500, cursor: 'pointer', border: 'none',
                background: tab === t ? '#111116' : 'transparent',
                color: tab === t ? '#eeeef5' : '#8e8ea8',
                boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,.4)' : 'none',
                transition: 'all .15s',
              }}
            >
              {t === 'plans' ? '📋 Plans' : '💳 Billing'}
            </button>
          ))}
        </div>

        {/* Plans tab */}
        {tab === 'plans' && (
          <PlanCardsSection
            allPlans={allPlans}
            currentPlan={currentPlan}
            onUpgrade={handleUpgrade}
            navigate={navigate}
          />
        )}

        {/* Billing tab */}
        {tab === 'billing' && (
          <>
            <div style={{ ...card, marginBottom: '14px' }}>
              <div style={sectionTitle}>Current Plan</div>
              <div style={{
                background: 'linear-gradient(135deg,rgba(99,102,241,.1),rgba(168,85,247,.1))',
                border: '1px solid rgba(99,102,241,.25)',
                borderRadius: '8px', padding: '14px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '10px',
              }}>
                <div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '17px', fontWeight: 700, color: '#eeeef5' }}>
                    {currentPlan ? formatPlanName(currentPlan.plan_name) : 'Starter'} Plan
                  </div>
                  <div style={{ fontSize: '12.5px', color: '#818cf8', marginTop: '2px', fontWeight: 600 }}>
                    {currentPlan ? (currentPlan.price === 0 ? 'Free' : `$${currentPlan.price}/mo`) : ''}
                  </div>
                  <div style={{ fontSize: '12.5px', color: '#8e8ea8', marginTop: '3px' }}>Renews Jan 1, 2027</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 700, background: 'rgba(99,102,241,.14)', color: '#818cf8', border: '1px solid rgba(99,102,241,.2)' }}>
                    Active
                  </span>
                  <button style={btn('primary')} onClick={() => setTab('plans')}>Change Plan</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showBillingModal && selectedPlan && (
        <BillingFormModal
          isOpen={showBillingModal}
          onClose={() => { setShowBillingModal(false); setSelectedPlan(null); }}
          planId={selectedPlan.plan_id}
          planName={formatPlanName(selectedPlan.plan_name)}
        />
      )}
      {showDowngradeBlocked && (
        <DowngradeBlockedModal onClose={() => setShowDowngradeBlocked(false)} />
      )}
    </div>
  );
};